import React, { JSX, useEffect, useState } from 'react';
import { CheckIcon, GearIcon, ImageIcon, MagnifyingGlassIcon, CodeIcon, SpinnerIcon } from '@phosphor-icons/react';
import { IconName } from '../../SocketProvider';

interface CircleProgressProps {
    progress: number;
    iconName: IconName;
}

const CircleProgress: React.FC<CircleProgressProps> = ({ progress: initialProgress, iconName }) => {
    const [progress, setProgress] = useState(initialProgress);
    const strokeWidth = 3;
    const radius = 20 - strokeWidth / 2;
    const circumference = 2 * Math.PI * radius;
    const strokeDashoffset = circumference - (progress / 100) * circumference;
    const viewBoxSize = radius * 2 + strokeWidth;

    useEffect(() => {
        const interval = setInterval(() => {
            setProgress((prevProgress) =>
                prevProgress < 100
                    ? prevProgress < initialProgress
                        ? prevProgress + 5
                        : prevProgress + 1
                    : prevProgress,
            );
        }, 200);

        return () => clearInterval(interval);
    }, [initialProgress]);

    const icons: { [key: string]: JSX.Element } = {
        Check: <CheckIcon className="icon-pulse" size={18} />,
        MagnifyingGlass: <MagnifyingGlassIcon className="icon-pulse" size={18} />,
        Image: <ImageIcon className="icon-pulse" size={18} />,
        Gear: <GearIcon className="icon-pulse" size={18} />,
        Code: <CodeIcon className="icon-pulse" size={18} />,
        Spinner: <SpinnerIcon className="icon-pulse" size={18} />,
    };

    return (
        <div
            style={{
                position: 'relative',
                width: `${viewBoxSize}px`,
                height: `${viewBoxSize}px`,
                display: 'inline-block',
                transition: 'all 3s',
            }}
        >
            <svg width={viewBoxSize} height={viewBoxSize} viewBox={`0 0 ${viewBoxSize} ${viewBoxSize}`}>
                <circle
                    cx={viewBoxSize / 2}
                    cy={viewBoxSize / 2}
                    r={radius}
                    fill="none"
                    stroke="#e0e0e0"
                    strokeWidth={strokeWidth}
                />
                <circle
                    cx={viewBoxSize / 2}
                    cy={viewBoxSize / 2}
                    r={radius}
                    fill="none"
                    stroke="#01c9b3"
                    strokeWidth={strokeWidth}
                    strokeLinecap="round"
                    transform={`rotate(-90 ${viewBoxSize / 2} ${viewBoxSize / 2})`}
                    strokeDasharray={`${circumference} ${circumference}`}
                    strokeDashoffset={strokeDashoffset}
                />
            </svg>
            <div
                style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                }}
            >
                {icons[iconName.toString()] ?? <CheckIcon className="icon-pulse" size={18} />}
            </div>
        </div>
    );
};

export default CircleProgress;
